import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { lumi } from '../lib/lumi'
import { FONT_OPTIONS, getFontById } from '../utils/fonts'
import { cleanTextStyle, TextStyle } from '../utils/textStyles'

type StyleMap = Record<string, TextStyle>
type RichMap = Record<string, string>

interface TextStyleContextValue {
  /** Style applique a tout le bloc de texte */
  getStyle: (key: string) => TextStyle
  /** HTML mis en forme caractere par caractere (vide si aucun) */
  getRich: (key: string) => string
  /** Enregistre le style du bloc et le texte mis en forme (html vide = aucun) */
  saveEntry: (key: string, style: TextStyle, richHtml: string) => Promise<void>
}

const TextStyleContext = createContext<TextStyleContextValue | null>(null)

/** Charge (une seule fois) la police Google correspondante si elle est necessaire. */
export function ensureFontLoaded(fontId?: string) {
  if (!fontId || typeof document === 'undefined') return
  const font = getFontById(fontId)
  if (!font.google) return
  const linkId = `ts-font-${font.id}`
  if (document.getElementById(linkId)) return
  const link = document.createElement('link')
  link.id = linkId
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${font.google}&display=swap`
  document.head.appendChild(link)
}

/** Charge les polices citees dans un texte mis en forme. */
export function ensureFontsInHtml(html: string) {
  if (!html) return
  FONT_OPTIONS.forEach((font) => {
    const first = font.stack.split(',')[0].replace(/["']/g, '').trim()
    if (first && html.includes(first)) ensureFontLoaded(font.id)
  })
}

export function TextStyleProvider({ children }: { children: ReactNode }) {
  const [styles, setStyles] = useState<StyleMap>({})
  const [rich, setRich] = useState<RichMap>({})
  const stylesRef = useRef<StyleMap>({})
  const richRef = useRef<RichMap>({})
  const recordIdRef = useRef<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        const { list } = await lumi.entities.site_content.list({
          filter: { page: 'global', section: 'textStyles' },
          limit: 1,
        })
        const record = list?.[0]
        if (record?._id) recordIdRef.current = record._id

        const storedStyles: StyleMap = record?.content?.styles || {}
        const cleaned: StyleMap = {}
        Object.keys(storedStyles).forEach((key) => {
          const style = cleanTextStyle(storedStyles[key])
          if (Object.keys(style).length > 0) {
            cleaned[key] = style
            ensureFontLoaded(style.fontFamily)
          }
        })

        const storedRich: RichMap = record?.content?.rich || {}
        const cleanedRich: RichMap = {}
        Object.keys(storedRich).forEach((key) => {
          if (typeof storedRich[key] === 'string' && storedRich[key]) {
            cleanedRich[key] = storedRich[key]
            ensureFontsInHtml(storedRich[key])
          }
        })

        stylesRef.current = cleaned
        richRef.current = cleanedRich
        setStyles(cleaned)
        setRich(cleanedRich)
      } catch (error) {
        console.error('Chargement des styles de texte:', error)
      }
    }
    load()
  }, [])

  const getStyle = useCallback((key: string): TextStyle => styles[key] || {}, [styles])
  const getRich = useCallback((key: string): string => rich[key] || '', [rich])

  const saveEntry = useCallback(async (key: string, style: TextStyle, richHtml: string) => {
    const cleaned = cleanTextStyle(style)
    const nextStyles: StyleMap = { ...stylesRef.current }
    if (Object.keys(cleaned).length === 0) {
      delete nextStyles[key]
    } else {
      nextStyles[key] = cleaned
      ensureFontLoaded(cleaned.fontFamily)
    }

    const nextRich: RichMap = { ...richRef.current }
    if (!richHtml) {
      delete nextRich[key]
    } else {
      nextRich[key] = richHtml
      ensureFontsInHtml(richHtml)
    }

    const now = new Date().toISOString()
    const payload = {
      page: 'global',
      section: 'textStyles',
      content: { styles: nextStyles, rich: nextRich },
      updatedAt: now,
    }

    if (recordIdRef.current) {
      await lumi.entities.site_content.update(recordIdRef.current, payload)
    } else {
      const created = await lumi.entities.site_content.create({
        ...payload,
        creator: lumi.auth.user?.userId || 'admin',
        creatorEmail: lumi.auth.user?.email || '',
        createdAt: now,
      })
      if (created?._id) recordIdRef.current = created._id
    }

    stylesRef.current = nextStyles
    richRef.current = nextRich
    setStyles(nextStyles)
    setRich(nextRich)
  }, [])

  return (
    <TextStyleContext.Provider value={{ getStyle, getRich, saveEntry }}>{children}</TextStyleContext.Provider>
  )
}

export function useTextStyles() {
  return useContext(TextStyleContext)
}
